// @flow
import React, { Component } from "react";
import loadImage from "blueimp-load-image";
import ImagusFacePicker from "ImagusFacePicker";
import classNames from "classnames";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { withStyles } from "@material-ui/core/styles";

import type {
  FaceDetectionResultType,
  EnrolmentType,
  EyeType
} from "../../../global/types/FaceType";
import {
  CancelButton,
  SubmitButton,
  UploadButton,
  LoadingProgress,
  GeneralModalForm,
  WebCamera,
  ConfirmCancelDialog
} from "imagus-components";
import {
  cropImageToFace,
  faceCentreFromEyes,
  radiusFromEyes
} from "../../../global/functions/CropImageToFace";
import CameraButton from "../../../global/components/CameraButton";
import { Grid } from "@material-ui/core";
import ImageQualityDetails from "./ImageQualityDetails";
import { EnrolmentInputModel } from "../../types/InputModels";
import CustomConfirmCancelDialog from "../../../global/components/CustomConfirmCancelDialog";

const styles = (theme: DictionaryType): DictionaryType => ({
  test: {
    display: "inline",
    paddingRight: "10px"
  }
});

type ImageUploadProcessClassesType = {
  test: DictionaryType
};
type ImageUploadProcessPropsType = {
  classes: ImageUploadProcessClassesType,
  detectedFaces: Array<FaceDetectionResultType>,
  uploadFile: Function,
  setImageError: Function,
  addEnrolement: (newEnrolment: EnrolmentInputModel) => void,
  buttonId: string,
  outerClass?: string,
  isFab?: boolean,
  personUid?: string,
  currentEnrolmentLength: number
};

type ImageUploadProcessStatesType = {
  loading: boolean,
  img: { url: string, canvas: HTMLCanvasElement } | null,
  token: string,
  eyes: Array<EyeType> | null,

  selected: number | null,
  dialogOpen: boolean,
  cameraOpen: boolean,
  warningOpen: boolean,
  qualityError: boolean
};

const initials: ImageUploadProcessStatesType = {
  loading: false,
  img: null,
  token: "",
  eyes: null,

  selected: null,
  dialogOpen: false,
  cameraOpen: false,
  qualityError: false,
  warningOpen: false
};

class ImageUploadProcess extends Component<
  ImageUploadProcessPropsType,
  ImageUploadProcessStatesType
> {
  constructor(props: ImageUploadProcessPropsType) {
    super(props);
    this.state = initials;
  }

  getEyesFromFaces = (
    faces: Array<FaceDetectionResultType>
  ): Array<EyeType> => {
    let eyes = faces.map((face: FaceDetectionResultType): EyeType => face.eyes);
    return eyes;
  };

  componentDidUpdate(prevProps: ImageUploadProcessPropsType) {
    if (prevProps.detectedFaces !== this.props.detectedFaces) {
      let eyes = this.getEyesFromFaces(this.props.detectedFaces);
      if (eyes !== this.state.eyes) {
        this.setState({ eyes: eyes, selected: eyes.length > 0 ? 0 : null });
      }
    }
  }

  resetForm = () => {
    this.props.setImageError("");
    this.setState({ ...initials });
  };

  uploadFileHandler = (e: *): * => {
    this.setState({ loading: true });
    this.props.setImageError("");
    let file;
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target) {
      file = e.target.files[0];
    }
    if (file) {
      this.props.uploadFile(
        file,
        (token: string): Function => this.completCall(token, file),
        this.uploadFailCall
      );
    } else {
      this.props.setImageError("Not Supported Image Format");
      this.setState({ loading: false });
    }
  };

  captureCamera = async (data: string) => {
    this.setState({ loading: true });
    this.props.setImageError("");

    let file;
    fetch(data)
      .then((res: Response) => res.blob())
      .then((blob: Blob) => {
        file = new Blob([blob], { type: "image/jpeg" });
        this.props.uploadFile(
          file,
          (token: string) => this.completCall(token, file),
          this.uploadFailCall
        );
      });
  };

  completCall = (token: string, file: File): * => {
    this.setState({ token: token });
    const loadingImage = loadImage(file, this.uploadDoneCall, {
      orientation: true,
      canvas: true
    });
  };

  uploadDoneCall = (canvas: HTMLCanvasElement): * => {
    const img = canvas.toDataURL("image/jpeg");
    this.setState({
      img: { url: img, canvas: canvas },
      loading: false,
      dialogOpen: true
    });
  };

  uploadFailCall = (msg: string): * => {
    this.props.setImageError(msg);
    this.setState({ loading: false });
  };

  updateFaces = (faces: Array<EyeType>, selection: number) => {
    this.setState({ eyes: faces, selected: selection });
  };

  faceEnrol = () => {
    if (this.state.qualityError) {
      this.setState({ warningOpen: true });
    } else if (this.state.eyes !== null && this.state.selected !== null) {
      const eyes = this.state.eyes[this.state.selected];
      const face = {
        eyes: eyes,
        centre: faceCentreFromEyes(eyes),
        radius: radiusFromEyes(eyes)
      };
      const img = cropImageToFace(this.state.img.canvas, face);
      this.props.addEnrolement({
        enrolmentId: Date.now(),
        token: this.state.token,
        imgUrl: img && img.src,
        eyes: eyes,
        nose: this.props.detectedFaces[this.state.selected].nose,
        mouth: this.props.detectedFaces[this.state.selected].mouth,
        size: this.props.detectedFaces[this.state.selected].size
      });
      this.resetForm();
    } else {
      this.props.setImageError("Please Select a Face.");
    }
  };

  syncQualityError = (status: boolean) => {
    this.setState({ qualityError: status });
  };

  render(): * {
    if (this.state.loading) {
      return (
        <div className={this.props.outerClass}>
          <LoadingProgress isLinear />
        </div>
      );
    } else if (this.state.img) {
      return (
        <Dialog
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
          open={this.state.dialogOpen}
          onClose={this.resetForm}
          fullWidth={true}
          maxWidth="md"
        >
          <DialogContent>
            {this.state.eyes ? (
              <Grid container spacing={8} style={{ alignItems: "flex-start" }}>
                <Grid item xs={12} md={7}>
                  <ImagusFacePicker
                    faces={this.state.eyes}
                    selection={this.state.selected}
                    facesDidUpdate={this.updateFaces}
                    imgUrl={this.state.img.url}
                  />
                </Grid>
                <ImageQualityDetails
                  faces={this.props.detectedFaces}
                  index={this.state.selected}
                  eyes={this.state.eyes[this.state.selected]}
                  setImageErrors={(test: string) => {}}
                  personUid={this.props.personUid}
                  currentEnrolmentLength={this.props.currentEnrolmentLength}
                  syncQualityError={this.syncQualityError}
                />
              </Grid>
            ) : (
              <LoadingProgress />
            )}
          </DialogContent>
          <DialogActions>
            <CancelButton onClick={this.resetForm} />
            <SubmitButton onClick={this.faceEnrol} id="submitBtn" />
          </DialogActions>
          <CustomConfirmCancelDialog
            open={this.state.warningOpen}
            handleModalStatus={(status: boolean) =>
              this.setState({ warningOpen: status })
            }
            title="Image Quality Check Failed!"
            deleteWord=" "
            itemName="use this photo"
            extraWord=" "
            deleteFunction={() => {
              this.setState({ qualityError: false }, this.faceEnrol);
            }}
          />
        </Dialog>
      );
    } else {
      return (
        <>
          <UploadButton
            id={this.props.buttonId}
            uploadFileActions={this.uploadFileHandler}
            isFab={this.props.isFab}
            outerClass={classNames(
              this.props.outerClass,
              this.props.classes.test
            )}
          />

          <CameraButton
            id={this.props.buttonId + "-camera"}
            open={this.state.cameraOpen}
            CameraActions={() => this.setState({ cameraOpen: true })}
          />
          <GeneralModalForm
            open={this.state.cameraOpen}
            title="Take Photo"
            handleModalStatus={(status: boolean) =>
              this.setState({ cameraOpen: status })
            }
            form={
              <WebCamera
                cancelCamera={() => this.setState({ cameraOpen: false })}
                captureCamera={this.captureCamera}
              />
            }
          />
        </>
      );
    }
  }
}

export default withStyles(styles)(ImageUploadProcess);
